import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    AssortmentTaskType,
    PaymentForm,
    ProductOrderItem,
    TaskFinGroupPhotosEntity,
    TaskRoute,
    TaskStatus,
} from "tm-shared/types";
import {
    isSubtaskFacingsPayload,
    isSubtaskFinGroupPhotosEntity,
    isSubtaskProductsMatrixPayload,
} from "tm-shared/utils";

import {
    MutateAssignmentsEntityPhotos,
    MutateAssortmentFinGroupPhotosEntityPhotos,
    MutateDMPPhotosProps,
    MutatePaidShelfSpacePhotosProps,
    MutatePromoTagPhotosPhotosProps,
    MutateUpdateMarketInfoPhotosProps,
    PatchFacingsEntityProps,
    PatchPaidShelfSpaceEntityProps,
    PatchProductsMatrixEntityProps,
    PatchPromoTagPhotosEntityProps,
    PatchTaskDMPEntityProps,
    PatchUpdateMarketInfoProps,
    ProgressState,
    SetAssortmentMatrixCompletionProps,
    SetAssortmentMatrixSubtaskPayloadProps,
    SetCheckoutDetailsPayload,
} from "./progress.types";
import {
    createDefaultSaleState,
    createTaskEntityBase,
    createTaskEntityPhoto,
    mutateEntityPhotosAndStatus,
    updatePhotoIds,
} from "./progress.utils";

const initialState: ProgressState = {
    [TaskRoute.Dmp]: {},
    [TaskRoute.AssortmentMatrix]: {},
    [TaskRoute.PromoTagPhotos]: {},
    [TaskRoute.UpdateMarketInfo]: {},
    [TaskRoute.CustomTask]: {},
    [TaskRoute.AddPhoto]: {},
    [TaskRoute.UpdateProductBalances]: {},
    [TaskRoute.RejectProducts]: {},
    [TaskRoute.SaleAdditional]: {},
    [TaskRoute.PaidShelfSpace]: {},

    checkoutDetails: {},
};

export const progressSlice = createSlice({
    name: "progress",
    initialState,
    reducers: {
        // ----------------------------------------- DMP -----------------------------------
        patchDMPEntity: (
            state,
            { payload }: PayloadAction<PatchTaskDMPEntityProps>,
        ) => {
            const { taskId, contractId, dmpId, patch } = payload;

            const taskEntry = (state.dmp[taskId] ??= {});

            const prev = taskEntry[dmpId] ?? {
                contractId,
                ...createTaskEntityPhoto(),
            };

            taskEntry[dmpId] = {
                ...prev,
                ...patch,
            };
        },

        mutateDMPPhotos: (
            state,
            { payload }: PayloadAction<MutateDMPPhotosProps>,
        ) => {
            const { taskId, dmpId, contractId } = payload;

            const taskEntry = (state.dmp[taskId] ??= {});

            const target = (taskEntry[dmpId] ??= {
                contractId,
                ...createTaskEntityPhoto(),
            });

            mutateEntityPhotosAndStatus(target, payload);
        },

        // ----------------------------------------- PromoTagPhotos -----------------------------------
        patchPromoTagPhotosEntity: (
            state,
            { payload }: PayloadAction<PatchPromoTagPhotosEntityProps>,
        ) => {
            const { taskId, barcode, patch } = payload;

            const taskEntry = (state.promoTagPhotos[taskId] ??= {});

            const prev = taskEntry[barcode] ?? {
                barcode,
                performerValue: null,
                ...createTaskEntityPhoto(),
            };

            taskEntry[barcode] = {
                ...prev,
                ...patch,
            };
        },

        mutatePromoTagPhotosPhotos: (
            state,
            { payload }: PayloadAction<MutatePromoTagPhotosPhotosProps>,
        ) => {
            const { taskId, barcode } = payload;

            const taskEntry = (state.promoTagPhotos[taskId] ??= {});

            const target = (taskEntry[barcode] ??= {
                ...createTaskEntityPhoto(),
                barcode,
                performerValue: null,
            });

            mutateEntityPhotosAndStatus(target, payload);

            if (target.status == TaskStatus.Incomplete) {
                target.performerValue = null;
            }
        },

        // ----------------------------------------- UpdateMarketInfo -----------------------------------
        patchUpdateMarketInfoEntity: (
            state,
            { payload }: PayloadAction<PatchUpdateMarketInfoProps>,
        ) => {
            const { taskId, subtaskId, patch, contractId } = payload;

            const taskEntry = (state.updateMarketInfo[taskId] ??= {});

            const prev = taskEntry[subtaskId] ?? {
                subtaskId,
                contractId,
                ...createTaskEntityPhoto(),
            };

            taskEntry[subtaskId] = {
                ...prev,
                ...patch,
            };
        },

        mutateUpdateMarketInfoPhotos: (
            state,
            { payload }: PayloadAction<MutateUpdateMarketInfoPhotosProps>,
        ) => {
            const { taskId, subtaskId, contractId } = payload;

            const taskEntry = (state.updateMarketInfo[taskId] ??= {});

            const target = (taskEntry[subtaskId] ??= {
                ...createTaskEntityPhoto(),
                subtaskId,
                ...(contractId ? { contractId } : {}),
            });

            mutateEntityPhotosAndStatus(target, payload);
        },

        // ------------------------------------ Assortments Matrix
        patchAssortmentFinGroupPhotosEntity: (
            state,
            {
                payload,
            }: PayloadAction<{
                taskId: string;
                contractId?: string;
                subtaskId: string;
                id: string;
                patch: Partial<Omit<TaskFinGroupPhotosEntity, "type" | "id">>;
            }>,
        ) => {
            const { taskId, id, patch, subtaskId, contractId } = payload;

            const taskEntry = (state.assortmentMatrix[taskId] ??= {});

            const currentSubtask = (taskEntry[subtaskId] ??= {
                type: AssortmentTaskType.FinGroupPhotos,
                subtaskId,
                contractId,
                completed: false,
                tasks: {},
            });

            if (!isSubtaskFinGroupPhotosEntity(currentSubtask)) return;

            const prev = currentSubtask.tasks[id] ?? {
                id,
                ...createTaskEntityPhoto(),
            };

            currentSubtask.tasks[id] = {
                ...prev,
                ...patch,
            };
        },

        mutateAssortmentFinGroupPhotosEntityPhotos: (
            state,
            {
                payload,
            }: PayloadAction<MutateAssortmentFinGroupPhotosEntityPhotos>,
        ) => {
            const { taskId, id, subtaskId, contractId } = payload;

            const taskEntry = (state.assortmentMatrix[taskId] ??= {});

            const currentSubtask = (taskEntry[subtaskId] ??= {
                type: AssortmentTaskType.FinGroupPhotos,
                subtaskId,
                contractId,
                completed: false,
                tasks: {},
            });

            if (!isSubtaskFinGroupPhotosEntity(currentSubtask)) return;

            const target = (currentSubtask.tasks[id] ??= {
                id,
                ...createTaskEntityPhoto(),
            });

            mutateEntityPhotosAndStatus(target, payload);
        },

        patchProductsMatrixEntity: (
            state,
            { payload }: PayloadAction<PatchProductsMatrixEntityProps>,
        ) => {
            const { taskId, subtaskId, barcode, patch, contractId } = payload;

            const taskEntry = (state.assortmentMatrix[taskId] ??= {});

            const subtaskEntry = (taskEntry[subtaskId] ??= {
                type: AssortmentTaskType.ProductsMatrix,
                completed: false,
                contractId,
                subtaskId,
                products: {},
            });

            if (!isSubtaskProductsMatrixPayload(subtaskEntry)) return;

            const prev =
                subtaskEntry.products[barcode] ?? createTaskEntityBase();

            subtaskEntry.products[barcode] = {
                ...prev,
                ...patch,
            };
        },

        patchFacingsEntity: (
            state,
            { payload }: PayloadAction<PatchFacingsEntityProps>,
        ) => {
            const { taskId, subtaskId, finGroup, patch, contractId } = payload;

            const taskEntry = (state.assortmentMatrix[taskId] ??= {});

            const subtaskEntry = (taskEntry[subtaskId] ??= {
                type: AssortmentTaskType.Facings,
                completed: false,
                contractId,
                subtaskId,
                facings: {},
            });

            if (!isSubtaskFacingsPayload(subtaskEntry)) return;

            const prev = subtaskEntry.facings[finGroup] ?? {
                amount: null,
            };

            subtaskEntry.facings[finGroup] = {
                ...prev,
                ...patch,
            };
        },

        setAssortmentMatrixCompletion: (
            state,
            { payload }: PayloadAction<SetAssortmentMatrixCompletionProps>,
        ) => {
            const { taskId, subtaskId, value } = payload;

            const subtaskEntry = state.assortmentMatrix?.[taskId]?.[subtaskId];

            if (!subtaskEntry) return;

            subtaskEntry.completed = value;
        },

        setAssortmentMatrixSubtaskPayload: (
            state,
            action: PayloadAction<SetAssortmentMatrixSubtaskPayloadProps>,
        ) => {
            const { taskId, subtaskId, payload } = action.payload;

            const taskEntry = (state.assortmentMatrix[taskId] ??= {});

            taskEntry[subtaskId] = payload;
        },

        // ---------------------------------- PaidShelfSpace

        patchPaidShelfSpaceEntity: (
            state,
            action: PayloadAction<PatchPaidShelfSpaceEntityProps>,
        ) => {
            const { taskId, patch } = action.payload;

            state.paidShelfSpace ??= {};
            const taskEntry = (state.paidShelfSpace[taskId] ??= {
                ...createTaskEntityPhoto(),
            });

            state.paidShelfSpace[taskId] = { ...taskEntry, ...patch };
        },

        mutatePaidShelfSpacePhotos: (
            state,
            action: PayloadAction<MutatePaidShelfSpacePhotosProps>,
        ) => {
            const { taskId } = action.payload;

            state.paidShelfSpace ??= {};
            const taskEntry = (state.paidShelfSpace[taskId] ??= {
                ...createTaskEntityPhoto(),
            });

            mutateEntityPhotosAndStatus(taskEntry, action.payload);
        },

        // ------------------------------------ Assignments (custom task)
        patchAssignmentsEntity: (
            state,
            {
                payload,
            }: PayloadAction<{
                taskId: string;
                patch: { comment?: string; status?: TaskStatus };
            }>,
        ) => {
            const { taskId, patch } = payload;

            const entry = state.customTask[taskId] ?? {
                comment: "",
                photos: [],
                status: TaskStatus.Completed,
            };

            state.customTask[taskId] = {
                ...entry,
                ...patch,
            };
        },

        mutateAssignmentsEntityPhotos: (
            state,
            { payload }: PayloadAction<MutateAssignmentsEntityPhotos>,
        ) => {
            const { taskId } = payload;

            const entry = (state.customTask[taskId] ??= {
                comment: "",
                photos: [],
                status: TaskStatus.Completed,
            });

            entry.photos ??= [];

            entry.photos = updatePhotoIds(entry.photos, payload);
        },

        // ------------------------------------ Add photo
        setAddPhotoTask: (
            state,
            action: PayloadAction<{ taskId: string; photoId: string }>,
        ) => {
            const { taskId, photoId } = action.payload;
            state.addPhoto[taskId] = photoId;
        },

        deleteAddPhotoTask: (
            state,
            action: PayloadAction<{ taskId: string }>,
        ) => {
            delete state.addPhoto[action.payload.taskId];
        },

        // ------------------------------------ updateProductBalances
        setUpdateProductBalancesInventory: (
            state,
            action: PayloadAction<{
                remains: Record<string, number>;
                taskId: string;
            }>,
        ) => {
            const { remains, taskId } = action.payload;

            state.updateProductBalances[taskId] = remains;
        },
        patchUpdateProductBalancesInventory: (
            state,
            action: PayloadAction<{
                barcode: string;
                count: number;
                taskId: string;
            }>,
        ) => {
            const { taskId, barcode, count } = action.payload;

            const balancesByTask = state.updateProductBalances;
            const taskBalances = (balancesByTask[taskId] ??= {});

            if (count > 0) {
                taskBalances[barcode] = count;
            } else {
                delete taskBalances[barcode];
            }
        },

        // ------------------------------------ Reject products
        setRejectedProduct: (
            state,
            {
                payload,
            }: PayloadAction<{
                taskId: string;
                product: ProductOrderItem;
            }>,
        ) => {
            const { taskId, product } = payload;

            const orderRejects = (state.rejectProducts[taskId] ??= {});

            if (product.count > 0) {
                orderRejects[product.barcode] = product;
            } else {
                delete orderRejects[product.barcode];
            }
        },

        // ------------------------------------ Additional sale
        setSaleProduct: (
            state,
            {
                payload,
            }: PayloadAction<{
                taskId: string;
                product: ProductOrderItem;
            }>,
        ) => {
            const { taskId, product } = payload;

            const entry = (state.saleAdditional[taskId] ??=
                createDefaultSaleState());

            if (product.count > 0) {
                entry.products[product.barcode] = product;
            } else {
                delete entry.products[product.barcode];
            }
        },
        setSalePaymentForm: (
            state,
            {
                payload,
            }: PayloadAction<{
                taskId: string;
                paymentForm: PaymentForm;
            }>,
        ) => {
            const { taskId, paymentForm } = payload;

            const entry = (state.saleAdditional[taskId] ??=
                createDefaultSaleState());

            entry.paymentForm = paymentForm;
        },
        addSaleIgnoredRemainingsProduct: (
            state,
            {
                payload,
            }: PayloadAction<{
                taskId: string;
                barcode: string;
            }>,
        ) => {
            const { taskId, barcode } = payload;

            const entry = (state.saleAdditional[taskId] ??=
                createDefaultSaleState());

            entry.ignoreRemainings = [...entry.ignoreRemainings, barcode];
        },

        // ------------------------------------ Common tasks
        clearTaskPayload: (
            state,
            action: PayloadAction<{ taskId: string }>,
        ) => {
            const { taskId } = action.payload;

            delete state.dmp[taskId];
            delete state.promoTagPhotos[taskId];
            delete state.updateMarketInfo[taskId];
            delete state.assortmentMatrix[taskId];
            delete state.customTask[taskId];
            delete state.addPhoto[taskId];
            delete state.updateProductBalances[taskId];
            delete state.rejectProducts[taskId];
            delete state.saleAdditional[taskId];
            delete state.paidShelfSpace?.[taskId];
        },

        // ------------------------------------ Checkout details
        setCheckoutDetails: (
            state,
            { payload }: PayloadAction<SetCheckoutDetailsPayload>,
        ) => {
            const { orderId } = payload;

            const entry = (state.checkoutDetails[orderId] ??= {
                comment: "",
                commentChecked: false,
                isCarrier: false,
                isSelfPickup: false,
                isPayOnDelivery: false,
            });

            if (payload.key === "comment") {
                entry.comment = payload.comment;
                entry.commentChecked = payload.checked;
                return;
            }

            entry[payload.key] = payload.checked;
        },
    },
});

export const {
    // ------------------------------------- DMP
    patchDMPEntity,
    mutateDMPPhotos,

    // ------------------------------------- PromoTagPhotos
    patchPromoTagPhotosEntity,
    mutatePromoTagPhotosPhotos,

    // ------------------------------------- UpdateMarketInfo
    patchUpdateMarketInfoEntity,
    mutateUpdateMarketInfoPhotos,

    // ------------------------------------ Assortments Matrix
    patchAssortmentFinGroupPhotosEntity,
    patchFacingsEntity,
    patchProductsMatrixEntity,
    mutateAssortmentFinGroupPhotosEntityPhotos,
    setAssortmentMatrixCompletion,
    setAssortmentMatrixSubtaskPayload,

    // ------------------------------------ PaidShelfSpace
    patchPaidShelfSpaceEntity,
    mutatePaidShelfSpacePhotos,

    // ------------------------------------ Assignments (custom task)
    patchAssignmentsEntity,
    mutateAssignmentsEntityPhotos,

    // ------------------------------------ Add photo
    setAddPhotoTask,
    deleteAddPhotoTask,

    // ------------------------------------ updateProductBalances
    setUpdateProductBalancesInventory,
    patchUpdateProductBalancesInventory,

    // ------------------------------------ Reject products
    setRejectedProduct,

    // ------------------------------------ Additional sale
    setSalePaymentForm,
    setSaleProduct,
    addSaleIgnoredRemainingsProduct,

    // ------------------------------------ Common tasks
    clearTaskPayload,

    // ------------------------------------ Checkout details
    setCheckoutDetails,
} = progressSlice.actions;

export const progressReducer = progressSlice.reducer;
